import type { PostPublic, PostMutationOptions } from '@repo/types/posts';

import { useInfiniteQueryResult } from '@repo/hooks/useInfiniteQueryResult';
import { type InfiniteData, type UseInfiniteQueryResult } from '@tanstack/react-query';
import { useMemo } from 'react';

import EmptyPostsList from '@/components/features/post/EmptyPostList';
import PostCard from '@/components/features/post/PostCard';
import PostCardSkeleton from '@/components/features/post/PostCardSkeleton';
import { InfiniteScroll } from '@/components/ui/infinite-scroll';
import { usePostReadTracker } from '@/hooks/posts';
import { useCredentials } from '@/store/auth-store';

interface PostListProps {
  query: UseInfiniteQueryResult<InfiniteData<PostPublic[], unknown>, Error>;
  options?: PostMutationOptions;
}

export default function PostList(postListProps: PostListProps) {
  const { query, options } = postListProps;

  const { data, isSuccess, isFetching, isLoading, hasNextPage, isError, fetchNextPage } = query;

  const { data: posts, isEmpty: postsIsEmpty } = useInfiniteQueryResult(data);

  // Only students are part of a post's audience, so only their reads are tracked.
  const { lvl } = useCredentials();
  const postIds = useMemo(() => posts.map((post) => post.id), [posts]);
  const { containerRef } = usePostReadTracker({ postIds, enabled: lvl === 0 });

  const next = async () => {
    await fetchNextPage();
  };

  return (
    <div ref={containerRef} className={'flex w-full flex-col gap-4'}>
      {posts.map((post) => (
        <div key={post.id} data-post-id={post.id}>
          <PostCard post={post} options={options} />
        </div>
      ))}
      {isSuccess && postsIsEmpty && <EmptyPostsList />}
      {isLoading && (
        <>
          <PostCardSkeleton />
          <PostCardSkeleton />
          <PostCardSkeleton />
        </>
      )}
      <InfiniteScroll hasNextPage={hasNextPage} isLoading={isLoading} isFetching={isFetching} next={next} threshold={0.25} root={null}>
        {hasNextPage && !isError && <PostCardSkeleton />}
      </InfiniteScroll>
    </div>
  );
}
